/**
 * Gallery Component
 * Handles filtering, lightbox, and load more functionality
 */

export default class Gallery {
    constructor(element) {
        this.element = element;
        this.filterBtns = document.querySelectorAll('.gallery-filter-btn');
        this.galleryItems = document.querySelectorAll('.gallery-item');
        this.lightbox = document.getElementById('galleryLightbox');
        this.lightboxImage = document.getElementById('lightboxImage');
        this.lightboxTitle = document.getElementById('lightboxTitle');
        this.lightboxDescription = document.getElementById('lightboxDescription');
        this.lightboxCategory = document.getElementById('lightboxCategory');
        this.lightboxCounter = document.getElementById('lightboxCounter');
        this.closeBtn = document.getElementById('lightboxClose');
        this.prevBtn = document.getElementById('lightboxPrev');
        this.nextBtn = document.getElementById('lightboxNext');
        this.loadMoreBtn = document.getElementById('loadMoreBtn');
        this.loadMoreContainer = document.getElementById('loadMoreContainer');
        
        this.currentFilter = 'all';
        this.currentIndex = 0;
        this.visibleItems = [];
        this.displayCount = 12;
        
        this.init();
    }
    
    init() {
        this.setupFilters();
        this.setupLightbox();
        this.setupLoadMore();
        this.updateVisibleItems();
    }
    
    setupFilters() {
        this.filterBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                
                // Update active state
                this.filterBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                
                // Filter items
                this.currentFilter = btn.dataset.filter;
                this.displayCount = 12; // Reset display count when filtering
                this.filterItems();
            });
        });
    }
    
    filterItems() {
        this.galleryItems.forEach(item => {
            const categories = item.dataset.categories.split(' ');
            
            if (this.currentFilter === 'all' || categories.includes(this.currentFilter)) {
                item.classList.remove('hidden');
            } else {
                item.classList.add('hidden');
            }
        });
        
        this.updateVisibleItems();
    }
    
    updateVisibleItems() {
        // Get all items that match the current filter (not hidden by filter)
        const filteredItems = Array.from(this.galleryItems).filter(item => 
            !item.classList.contains('hidden')
        );
        
        // Apply load more logic - show only up to displayCount
        filteredItems.forEach((item, index) => {
            if (index < this.displayCount) {
                item.style.display = 'inline-block';
            } else {
                item.style.display = 'none';
            }
        });
        
        // Update visible items array to only include currently displayed items
        this.visibleItems = filteredItems.filter((item, index) => index < this.displayCount);
        
        // Show/hide load more button
        if (this.loadMoreContainer) {
            if (this.displayCount < filteredItems.length) {
                this.loadMoreContainer.style.display = 'block';
            } else {
                this.loadMoreContainer.style.display = 'none';
            }
        }
        
    }
    
    setupLoadMore() {
        if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.displayCount += 9;
                this.updateVisibleItems();
            });
        }
    }
    
    setupLightbox() {
        // Open lightbox on item click
        this.galleryItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                if (!item.classList.contains('hidden') && item.style.display !== 'none') {
                    const visibleIndex = this.visibleItems.indexOf(item);
                    if (visibleIndex !== -1) {
                        this.openLightbox(visibleIndex);
                    }
                }
            });
        });
        
        // Close lightbox
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.closeLightbox());
        }
        
        // Previous/Next buttons
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.goToPrevious());
        }
        
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.goToNext());
        }
        
        // Close on background click
        if (this.lightbox) {
            this.lightbox.addEventListener('click', (e) => {
                if (e.target === this.lightbox) {
                    this.closeLightbox();
                }
            });
        }
        
        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (this.lightbox && this.lightbox.classList.contains('active')) {
                if (e.key === 'Escape') {
                    this.closeLightbox();
                } else if (e.key === 'ArrowLeft') {
                    this.goToPrevious();
                } else if (e.key === 'ArrowRight') {
                    this.goToNext();
                }
            }
        });
    }
    
    openLightbox(index) {
        this.currentIndex = index;
        this.updateLightboxContent();
        this.lightbox.classList.add('active');
        document.body.style.overflow = 'hidden';
    }
    
    closeLightbox() {
        this.lightbox.classList.remove('active');
        document.body.style.overflow = '';
    }
    
    goToPrevious() {
        this.currentIndex = this.currentIndex === 0 ? this.visibleItems.length - 1 : this.currentIndex - 1;
        this.updateLightboxContent();
    }
    
    goToNext() {
        this.currentIndex = this.currentIndex === this.visibleItems.length - 1 ? 0 : this.currentIndex + 1;
        this.updateLightboxContent();
    }
    
    updateLightboxContent() {
        const item = this.visibleItems[this.currentIndex];
        const img = item.querySelector('.gallery-item__image');
        const title = item.querySelector('.gallery-item__title');
        const category = item.querySelector('.gallery-item__category');
        const description = item.dataset.description || '';
        const imageTitle = item.dataset.imageTitle || title?.textContent || '';
        
        if (this.lightboxImage && img) {
            this.lightboxImage.src = img.src;
            this.lightboxImage.alt = img.alt;
        }
        
        if (this.lightboxTitle) {
            this.lightboxTitle.textContent = imageTitle;
        }
        
        if (this.lightboxCategory && category) {
            this.lightboxCategory.textContent = category.textContent;
        }
        
        if (this.lightboxDescription) {
            this.lightboxDescription.textContent = description;
        }
        
        if (this.lightboxCounter) {
            this.lightboxCounter.textContent = `Image ${this.currentIndex + 1} of ${this.visibleItems.length}`;
        }
    }
    
    fetchDescription(postId) {
        // Not needed anymore as we use data attribute
    }
}
